import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../environment/environment';
import { UserService } from './user.service';
import { TranslationService } from './translate.service';
import { defaultPreference, UserPreference, UserPreferenceLanguage, UserPreferenceSettings } from '../model/user/user-preference';

@Injectable({
  providedIn: 'root',
})
export class UserPreferenceService {
  private readonly httpClient = inject(HttpClient);
  private readonly userService = inject(UserService);
  private readonly translationService = inject(TranslationService);
  private readonly endpoint = environment.APIBase + 'User/preference';
  private readonly cacheKeyPrefix = 'userPreference';

  private readonly preference = signal<UserPreferenceSettings>(this.getSessionPreference());
  private initializePromise: Promise<UserPreferenceSettings> | null = null;

  public readonly currentPreference = this.preference.asReadonly();
  public readonly isDarkMode = computed(() => this.preference().isDarkMode);
  public readonly language = computed(() => this.preference().language);
  public readonly showHeroSection = computed(() => this.preference().showHeroSection);

  initialize(): Promise<UserPreferenceSettings> {
    if (this.initializePromise) {
      return this.initializePromise;
    }

    const userId = this.getPreferenceUserId();
    if (!userId) {
      this.applyPreference(this.preference());
      return Promise.resolve(this.preference());
    }

    const cachedPreference = this.getCachedPreference(userId);
    if (cachedPreference) {
      this.applyPreference(cachedPreference);
    }

    this.initializePromise = firstValueFrom(
      this.httpClient.get<UserPreference | null>(`${this.endpoint}?userId=${encodeURIComponent(userId)}`)
    )
      .then((preference) => {
        const settings = preference ?? { ...defaultPreference, userId };
        this.applyPreference(settings);
        this.cachePreference(userId, settings);
        return this.preference();
      })
      .catch((error) => {
        console.error('Error loading user preference', error);
        this.applyPreference(this.preference());
        return this.preference();
      });

    return this.initializePromise;
  }

  setDarkMode(isDarkMode: boolean): Promise<UserPreferenceSettings> {
    return this.updatePreference({ isDarkMode });
  }

  setLanguage(language: UserPreferenceLanguage): Promise<UserPreferenceSettings> {
    return this.updatePreference({ language });
  }

  setShowHeroSection(showHeroSection: boolean): Promise<UserPreferenceSettings> {
    return this.updatePreference({ showHeroSection });
  }

  private updatePreference(patch: Partial<UserPreferenceSettings>): Promise<UserPreferenceSettings> {
    const userId = this.getPreferenceUserId();
    const nextPreference = {
      ...this.preference(),
      ...patch,
    };

    this.applyPreference(nextPreference);

    if (!userId) {
      return Promise.resolve(nextPreference);
    }

    const body: UserPreference = {
      userId,
      ...nextPreference,
    };

    return firstValueFrom(this.httpClient.post<UserPreference>(this.endpoint, body))
      .then((savedPreference) => {
        this.applyPreference(savedPreference);
        this.cachePreference(userId, savedPreference);
        return this.preference();
      })
      .catch((error) => {
        console.error('Error saving user preference', error);
        return this.preference();
      });
  }

  private applyPreference(preference: UserPreferenceSettings): void {
    this.preference.set({
      isDarkMode: preference.isDarkMode,
      language: preference.language,
      showHeroSection: preference.showHeroSection,
    });

    sessionStorage.setItem('dark', preference.isDarkMode ? 'true' : 'false');
    sessionStorage.setItem('language', preference.language);
    sessionStorage.setItem('heroVisible', preference.showHeroSection ? '1' : '0');
    document.documentElement.classList.toggle('dark', preference.isDarkMode);
    this.translationService.setLanguage(preference.language);
  }

  private getSessionPreference(): UserPreferenceSettings {
    const storedLanguage = sessionStorage.getItem('language');

    return {
      isDarkMode: sessionStorage.getItem('dark') === 'true',
      language: this.isSupportedLanguage(storedLanguage) ? storedLanguage : defaultPreference.language,
      showHeroSection: sessionStorage.getItem('heroVisible') === '1',
    };
  }

  private isSupportedLanguage(language: string | null): language is UserPreferenceLanguage {
    return language === 'en-US' || language === 'de-DE';
  }

  private getCachedPreference(userId: string): UserPreferenceSettings | null {
    const rawPreference = localStorage.getItem(this.getCacheKey(userId));
    if (!rawPreference) {
      return null;
    }

    try {
      const preference = JSON.parse(rawPreference) as Partial<UserPreferenceSettings>;
      const language = preference.language;
      if (
        typeof preference.isDarkMode !== 'boolean' ||
        typeof preference.showHeroSection !== 'boolean' ||
        !language ||
        !this.isSupportedLanguage(language)
      ) {
        return null;
      }

      return {
        isDarkMode: preference.isDarkMode,
        language,
        showHeroSection: preference.showHeroSection,
      };
    } catch {
      return null;
    }
  }

  private cachePreference(userId: string, preference: UserPreferenceSettings): void {
    localStorage.setItem(this.getCacheKey(userId), JSON.stringify({
      isDarkMode: preference.isDarkMode,
      language: preference.language,
      showHeroSection: preference.showHeroSection,
    }));
  }

  private getCacheKey(userId: string): string {
    return `${this.cacheKeyPrefix}:${userId}`;
  }

  private getPreferenceUserId(): string {
    const user = this.userService.currentUser();
    return user.impersonatedBy?.subject ?? user.subject;
  }
}
